import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack } from 'react-icons/bi';
import { parivartanAPI } from '../../../services/parivartanAPI';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useAltF1TestFill } from '../../../hooks/useAltF1TestFill';
import { dummyParivartanInviteUserForm } from '../../../utils/testFormDummies';
import { clearWorkspaceAccess } from '../../../hooks/useWorkspaceAccess';
import { usersListPath } from './settingsPaths';
import { loadOrganisationSettings } from '../../../utils/organisationSettings';
import '../tender/CustomerMaster.scss';
import './UserFormPage.scss';

const EMPTY = {
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  roles: ['CONTRIBUTOR'],
  permissionGrants: '',
  permissionDenials: '',
  isActive: true
};

const monogram = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'New';
  if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
};

const splitList = (value) => value.split(',').map((item) => item.trim()).filter(Boolean);

const Field = ({ label, wide, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>{label}</Form.Label>
    {children}
  </Form.Group>
);

const UserFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const currentUser = useSelector((state) => state.auth.user);
  const listPath = usersListPath(location.pathname);
  const isCreate = location.pathname.endsWith('/new');
  const ownRecord = !isCreate && String(userId) === String(currentUser?.id || currentUser?._id || '');
  const [form, setForm] = useState(EMPTY);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [passwordMinLength, setPasswordMinLength] = useState(6);

  useDocumentTitle(isCreate ? 'New user' : (form.name || 'Edit user'));

  useEffect(() => {
    loadOrganisationSettings().then((data) => {
      const floor = Number(data?.settings?.passwordMinLength);
      if (floor >= 6) setPasswordMinLength(floor);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const rolesRes = await parivartanAPI.listRolesCatalog();
        if (cancelled) return;
        setRoles(rolesRes.data?.data?.roles || []);
        if (!isCreate) {
          const userRes = await parivartanAPI.getUser(userId);
          const row = userRes.data?.data?.user;
          if (!row) {
            setError('That user was not found.');
          } else {
            setForm({
              name: row.name || '',
              email: row.email || '',
              password: '',
              confirmPassword: '',
              roles: row.roles || [],
              permissionGrants: (row.permissionGrants || []).join(', '),
              permissionDenials: (row.permissionDenials || []).join(', '),
              isActive: row.isActive !== false
            });
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Unable to open this user');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isCreate, userId]);

  useAltF1TestFill(isCreate && !loading, () => setForm((current) => ({
    ...current,
    ...dummyParivartanInviteUserForm(),
    password: '',
    confirmPassword: '',
    roles: current.roles?.length ? current.roles : ['CONTRIBUTOR']
  })));

  const toggleRole = (role) => {
    if (ownRecord) return;
    setForm((current) => {
      const next = current.roles || [];
      return {
        ...current,
        roles: next.includes(role) ? next.filter((item) => item !== role) : [...next, role]
      };
    });
  };

  const save = async (event) => {
    event.preventDefault();
    setError('');
    if (!form.name.trim()) {
      setError('Enter the person’s name.');
      return;
    }
    if (isCreate) {
      if (!form.email.trim()) {
        setError('Enter an email address.');
        return;
      }
      const floor = passwordMinLength;
      if (form.password.length < floor) {
        setError(`Password must be at least ${floor} characters.`);
        return;
      }
      if (form.password !== form.confirmPassword) {
        setError('The two passwords do not match.');
        return;
      }
      if (!form.roles.length) {
        setError('Choose at least one role.');
        return;
      }
    } else if (!ownRecord && !form.roles.length) {
      setError('Choose at least one role.');
      return;
    }

    setSaving(true);
    try {
      if (isCreate) {
        await parivartanAPI.createUser({
          email: form.email.trim(),
          name: form.name.trim(),
          password: form.password,
          roles: form.roles
        });
      } else if (ownRecord) {
        await parivartanAPI.updateUser(userId, { name: form.name.trim() });
        clearWorkspaceAccess();
      } else {
        await parivartanAPI.updateUser(userId, {
          name: form.name.trim(),
          roles: form.roles,
          permissionGrants: splitList(form.permissionGrants),
          permissionDenials: splitList(form.permissionDenials),
          isActive: form.isActive
        });
        clearWorkspaceAccess();
      }
      navigate(listPath);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save this user');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cust-page cust-page--loading">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  const mark = monogram(form.name);
  const heading = isCreate ? 'New user' : (form.name || 'Edit user');
  const activeRoles = roles.filter((role) => role.isActive !== false);
  const heldInactive = roles.filter((role) => role.isActive === false && (form.roles || []).includes(role.role));
  const selectedRoles = activeRoles.filter((role) => (form.roles || []).includes(role.role));

  return (
    <div className="cust-page user-form">
      <form className="cust-record" onSubmit={save}>
        <header className="cust-mast">
          <div className="cust-mast__identity">
            <button type="button" className="cust-page__back" onClick={() => navigate(listPath)}>
              <BiArrowBack />
              Users
            </button>
            <div className="cust-mast__row">
              <span className={`cust-mark${mark.length > 2 ? ' cust-mark--wide' : ''}`} aria-hidden>{mark}</span>
              <div className="cust-mast__copy">
                <p className="cust-mast__eyebrow">{isCreate ? 'User directory' : 'Editing user'}</p>
                <h1>{heading}</h1>
                <div className="cust-mast__meta">
                  {form.email ? <span className="cust-chip">{form.email}</span> : <span className="cust-chip">Email on save</span>}
                  <span className={`cust-status cust-status--${form.isActive ? 'active' : 'inactive'}`}>
                    {form.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <span className="cust-chip">{form.roles.length} {form.roles.length === 1 ? 'role' : 'roles'}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="cust-mast__actions">
            <Button size="sm" variant="outline-primary" type="button" onClick={() => navigate(listPath)} disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" type="submit" disabled={saving || !form.name.trim() || (isCreate && (!form.email.trim() || !form.password))}>
              {saving ? 'Saving…' : isCreate ? 'Create user' : 'Save changes'}
            </Button>
          </div>
        </header>

        {error ? <Alert variant="danger" className="cust-record__alert">{error}</Alert> : null}

        <div className="cust-record__body">
          <div className="cust-record__main">
            <section className="cust-section">
              <header className="cust-section__head">
                <span>01</span>
                <div>
                  <h2>Account</h2>
                  <p>{isCreate ? 'The person signs in with this email and password.' : 'The email is the sign-in address and stays with this account.'}</p>
                </div>
              </header>
              <div className="cust-fields">
                <Field label="Full name" wide={6}>
                  <Form.Control
                    value={form.name}
                    placeholder="Display name"
                    onChange={(event) => setForm({ ...form, name: event.target.value })}
                    required
                  />
                </Field>
                <Field label="Email" wide={6}>
                  <Form.Control
                    type="email"
                    placeholder="name@dhruvconsultancy.in"
                    value={form.email}
                    autoComplete="off"
                    disabled={!isCreate}
                    required={isCreate}
                    onChange={(event) => setForm({ ...form, email: event.target.value })}
                  />
                </Field>
                {isCreate ? (
                  <>
                    <Field label="Password" wide={6}>
                      <Form.Control
                        type="password"
                        autoComplete="new-password"
                        value={form.password}
                        onChange={(event) => setForm({ ...form, password: event.target.value })}
                        required
                      />
                      <p className="user-form__hint">At least {passwordMinLength} characters. Share it with the person directly.</p>
                    </Field>
                    <Field label="Confirm password" wide={6}>
                      <Form.Control
                        type="password"
                        autoComplete="new-password"
                        value={form.confirmPassword}
                        onChange={(event) => setForm({ ...form, confirmPassword: event.target.value })}
                        required
                      />
                    </Field>
                  </>
                ) : null}
              </div>
            </section>

            <section className="cust-section">
              <header className="cust-section__head">
                <span>02</span>
                <div>
                  <h2>Roles</h2>
                  <p>Choose every active role this person should hold. A role adds its permissions together. Inactive roles are not offered here.</p>
                </div>
              </header>
              {ownRecord ? (
                <Alert variant="light" className="border py-2 small">
                  Another platform administrator changes the roles on your own account.
                </Alert>
              ) : null}
              <div className="user-form__roles">
                {activeRoles.map((role) => {
                  const selected = (form.roles || []).includes(role.role);
                  return (
                    <Form.Check
                      key={role.role}
                      type="checkbox"
                      id={`user-role-${role.role}`}
                      className={`user-form__role${selected ? ' is-selected' : ''}${ownRecord ? ' is-locked' : ''}`}
                      label={role.name || role.role}
                      checked={selected}
                      disabled={ownRecord}
                      onChange={() => toggleRole(role.role)}
                    />
                  );
                })}
              </div>
              {heldInactive.length ? (
                <p className="user-form__hint mt-2">
                  Already on this account, and hidden while inactive: {heldInactive.map((role) => role.name || role.role).join(', ')}.
                  Mark the role active under Roles & access if it should be offered again.
                </p>
              ) : null}
            </section>

            {isCreate ? null : (
              <section className="cust-section">
                <header className="cust-section__head">
                  <span>03</span>
                  <div>
                    <h2>Permission overrides</h2>
                    <p>Optional. Grants add a permission. Denials remove one even when a role includes it.</p>
                  </div>
                </header>
                <div className="cust-fields">
                  <Field label="Grants" wide={12}>
                    <Form.Control
                      placeholder="tender.register.read, tender.update"
                      value={form.permissionGrants}
                      disabled={ownRecord}
                      onChange={(event) => setForm({ ...form, permissionGrants: event.target.value })}
                    />
                  </Field>
                  <Field label="Denials" wide={12}>
                    <Form.Control
                      placeholder="Permissions to remove"
                      value={form.permissionDenials}
                      disabled={ownRecord}
                      onChange={(event) => setForm({ ...form, permissionDenials: event.target.value })}
                    />
                  </Field>
                </div>
              </section>
            )}
          </div>

          <aside className="cust-rail">
            <div className="cust-rail__card">
              <p className="cust-rail__label">{isCreate ? 'New account' : 'This account'}</p>
              <h2>{form.name.trim() || 'Unnamed user'}</h2>
              <p className="cust-rail__place">{form.email || 'Email is entered on the left'}</p>
              {isCreate ? (
                <p className="user-form__hint">Saving creates an active account. No invitation email is sent.</p>
              ) : (
                <div className="user-form__switch mt-3">
                  <div>
                    <div className="fw-semibold small">Active account</div>
                    <div className="text-muted small">Inactive people cannot sign in.</div>
                  </div>
                  <Form.Check
                    type="switch"
                    id="user-active-switch"
                    checked={form.isActive}
                    disabled={ownRecord}
                    onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                    label=""
                  />
                </div>
              )}
              {selectedRoles.length ? (
                <ul className="user-form__role-list">
                  {selectedRoles.map((role) => <li key={role.role}>{role.name || role.role}</li>)}
                </ul>
              ) : (
                <p className="user-form__hint">No role selected yet.</p>
              )}
            </div>
          </aside>
        </div>
      </form>
    </div>
  );
};

export default UserFormPage;
